/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import { Avatar, Grid } from '@mui/material';
import { DialogForm, IDialogActions, ISnackbarAlert, SelectField, SnackbarAlert, TextFieldInput, TextFieldReadOnly } from '../../../shared/components';
import { GenericService } from '../../../shared/services';
import { ListId, LocalStorange } from '../../../shared/environment';
import { AccessRoleDTO, AccessRoleFilterDTO, CommentDTO, CommentFilterDTO, UserDTO, ValueListDTO } from '../../../shared/interfaces';
import { formatDateTime } from '../../../shared/utils';
import { useTranslation } from 'react-i18next';

interface ICommentMaintenanceProps {
    ownerId: string;
    data: any;
    setIsOpenMaintenance: (value: boolean) => void;
    isOpenMaintenance: boolean;
    setActionExecuted: (value: boolean) => void;
}

export const CommentMaintenance: React.FC<ICommentMaintenanceProps> = ({ ownerId, data, isOpenMaintenance, setIsOpenMaintenance, setActionExecuted }) => {

    const [userList, setUserList] = useState<UserDTO[]>([]);
    const [commentDTO, setCommentDTO] = useState<CommentDTO>();

    const [loading, setLoading] = useState(false);
    const [snackBarAlert, setSnackBarAlert] = useState<ISnackbarAlert>();
    const [isOpenMessage, setIsOpenMessage] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');
    const { t } = useTranslation();

    useEffect(() => {
        setCommentDTO({
            ...commentDTO,
            id: '',
            commentary: '',
            emails: '',
            ownerId: ownerId,
        } as CommentDTO);
    }, [data, isOpenMaintenance]);

    useEffect(() => {
        if (isOpenMaintenance) {
            handleUserGetAllAsync();
            handleGetCommentByIdAsync();
            handleGetPermission(ListId.infoSphere.commentId, setAccessRolePermissionDTO);
        }
    }, [data, isOpenMaintenance]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync', accessRoleFilterDTO, setActionExecuted, setLoading, undefined, setList,);
    }, []);

    const handleUserGetAllAsync = useCallback(async () => {
        const userFilterDTO = {
            commentId: '',
        };
        GenericService.GetAllViewAsync1('authsphere.user', userFilterDTO, setActionExecuted, setLoading, setUserList, showMessage, false);
    }, []);

    const handleGetCommentByIdAsync = useCallback(async () => {
        if (data && data.id) {
            await GenericService.GetByIdAsync1('infosphere.comment', data.id, setLoading, setCommentDTO, showMessage);
        }
    }, [data, ownerId]);

    const handleSaveClick = useCallback(async () => {
        const dto = commentDTO as CommentDTO;

        if (commentDTO?.id === '' || commentDTO?.id === undefined) {
            dto.createdById = userInfo.user.id;
            dto.ownerId = ownerId;
            GenericService.AddAsync1('infosphere.comment', dto, setCommentDTO, setActionExecuted, setLoading, showMessage);
        }
        else {
            if (!accessRolePermissionDTO?.allowUpdate) {
                showMessage(t('message.allowUpdate'), 'info');
                return;
            }

            dto.updatedById = userInfo.user.id;
            GenericService.UpdateAsync1('infosphere.comment', dto, setActionExecuted, setLoading, showMessage);
        }
    }, [commentDTO]);

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarAlert(msg);
        setIsOpenMessage(true);

        if (type != 'success')
            setIsOpenMaintenance(true);
    }
    const actions: IDialogActions[] = [
        { id: 'save', text: t('comment.maintenanceButton.save'), icon: 'save_outlined', iconColor: 'primary', setAction: handleSaveClick, isClose: isOpenMaintenance },
        { id: 'close', text: t('comment.maintenanceButton.close'), icon: 'close_outlined', iconColor: 'secondary', setAction: () => void undefined },
    ];

    return (
        <React.Fragment>
            {isOpenMessage && (
                <SnackbarAlert
                    snackBarAlert={snackBarAlert}
                    setIsOpen={setIsOpenMessage}
                    isOpen={isOpenMessage}
                />
            )}
            <DialogForm
                title={t('comment.detailsName')}
                icon='comment_outlined'
                setIsOpen={setIsOpenMaintenance}
                isOpen={isOpenMaintenance}
                fullWidth={true}
                maxWidth={'lg'}
                actions={actions}
                loading={loading}
            >
                <React.Fragment>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={12}>
                            <TextFieldInput
                                id='commentary'
                                label={t('comment.maintenance.commentary')}
                                placeholder={t('comment.maintenance.commentary')}
                                value={commentDTO?.commentary}
                                setValue={setCommentDTO}
                                obj={commentDTO}
                                required={true}
                                autoFocus={true}
                                multiline={true}
                                rows={6}
                                readonly ={ commentDTO?.id ? userInfo.user.id == commentDTO?.createdById ? false : true : false}
                                valueList={userList}
                                labelKey='userName'
                                valueKey='email'
                                isUserFollowUp ={true}
                            />
                        </Grid>
                        {commentDTO?.id ? (
                            <React.Fragment>
                                <Grid item xs={12} md={4}>
                                    <TextFieldReadOnly
                                        id={'id'}
                                        label={t('comment.maintenance.id')}
                                        value={commentDTO.id}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'createdById'}
                                        label={t('comment.maintenance.createdBy')}
                                        value={userList.find(x => x.id === commentDTO.createdById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'creationDateTime'}
                                        label={t('comment.maintenance.creationDateTime')}
                                        value={formatDateTime(String(commentDTO.creationDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updatedById'}
                                        label={t('comment.maintenance.updatedBy')}
                                        value={userList.find(x => x.id === commentDTO.updatedById)?.userName}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextFieldReadOnly
                                        id={'updateDateTime'}
                                        label={t('comment.maintenance.updateDateTime')}
                                        value={formatDateTime(String(commentDTO.updateDateTime))}
                                        hasDivider={false}
                                        hasToolTip={true}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : undefined}
                    </Grid>
                </React.Fragment>
            </DialogForm >
        </React.Fragment >
    );
};
